import { Decimal, type MoneyInput } from '@bruhat/shared';
import { cn } from '@/lib/cn';
import { formatINR, formatINRCompact } from '@/lib/format';

interface MoneyProps {
  value: MoneyInput;
  /** Colour negatives red and zeros grey. */
  signed?: boolean;
  compact?: boolean;
  decimals?: number;
  showSymbol?: boolean;
  className?: string;
  /** Render "Dr"/"Cr" instead of a minus sign, as a ledger does. */
  drCr?: boolean;
}

/**
 * The single place money is rendered. Values arrive as decimal STRINGS and are
 * formatted with Indian digit grouping in a tabular font so columns align.
 */
export function Money({
  value,
  signed = false,
  compact = false,
  decimals = 2,
  showSymbol = true,
  className,
  drCr = false,
}: MoneyProps) {
  const amount = new Decimal(value ?? 0);

  if (drCr) {
    const suffix = amount.isNegative() ? 'Cr' : 'Dr';
    return (
      <span className={cn('tabular', className)}>
        {formatINR(amount.abs(), { decimals, symbol: showSymbol })}
        <span className="ml-1 text-2xs text-ink-subtle dark:text-slate-500">{suffix}</span>
      </span>
    );
  }

  const text = compact
    ? formatINRCompact(amount)
    : formatINR(amount, { decimals, symbol: showSymbol });

  return (
    <span
      className={cn(
        'tabular',
        signed && amount.isNegative() && 'text-negative',
        signed && amount.isZero() && 'text-ink-subtle dark:text-slate-500',
        signed && amount.isPositive() && 'text-positive',
        className,
      )}
    >
      {text}
    </span>
  );
}

/** Variance display: positive is favourable, negative is an overrun. */
export function Variance({
  value,
  percent,
  className,
}: {
  value: MoneyInput;
  percent?: MoneyInput;
  className?: string;
}) {
  const amount = new Decimal(value ?? 0);
  const favourable = !amount.isNegative();

  return (
    <span className={cn('tabular inline-flex items-baseline gap-1', className)}>
      <span className={favourable ? 'text-positive' : 'text-negative'}>
        {favourable ? '' : '−'}
        {formatINRCompact(amount.abs())}
      </span>
      {percent !== undefined && (
        <span className="text-2xs text-ink-subtle dark:text-slate-500">
          ({new Decimal(percent ?? 0).abs().toDecimalPlaces(1).toString()}%)
        </span>
      )}
    </span>
  );
}
